import { useRef } from "react";
import { useSelector } from "react-redux";
import "./PiInteractionTitlePreview.css";
import PiQuizOutlineIcon from "../../../icons/PiQuizOutlineIcon";
import { selectCurrentLanguage } from "../../../app/slices/TrainingLocalizationSlice";
import { selectSequenceVariables } from "../../../app/slices/TrainingSlice";
import VariableSuggestionPopper from "../../variables/VariableSuggestionPopper";
import VariableTextOverlay from "../../variables/VariableTextOverlay";
import useVariableTextareaController from "../../variables/useVariableTextareaController.hook";

interface PiInteractionTitlePreviewProps {
  value: string;
  onChange: (newValue: string, shortcode?: string) => void;
  placeholder: string;
  showQuizIcon?: boolean;
  showStarIcons?: boolean;
}

export default function PiInteractionTitlePreview(
  props: PiInteractionTitlePreviewProps
) {
  const {
    value,
    onChange,
    placeholder,
  } = props;

  const currLang = useSelector(selectCurrentLanguage);
  const variables = useSelector(selectSequenceVariables);
  const inputWrapperRef = useRef<HTMLDivElement>(null);
  const {
    textareaRef,
    inputValue,
    warningText,
    highlightedSegments,
    scrollTop,
    scrollLeft,
    handleFocus,
    handleChange,
    handleBlur,
    handleClick,
    handleKeyUp,
    handleScroll,
    handleWheel,
    handleKeyDown,
    flushPendingValue,
    suggestions,
  } = useVariableTextareaController({
    value,
    onCommit: (nextValue) => onChange(nextValue, currLang.shortcode),
    variables,
    debounceMs: 250,
    flushOnCleanupKeys: [currLang.shortcode],
  });

  return (
    <div className="piinteractiontitlepreview-shell">
      <div
        className={
          "flex w-full border-b-2 border-solid rounded-t-[5px] h-[60px] gap-[16px] px-[20px] bg-interaction-softBackground border-interaction-primary hover:bg-interaction-primary-washed"
        }
      >
        <div
          className={"my-auto font-semibold text-interaction-primary text-[30px]"}
        >
          <PiQuizOutlineIcon
            className={"fill-interaction-primary"}
            height={30}
            width={30}
          />
        </div>
        <div className={"flex my-auto w-full bg-none h-[30px]"}>
          <div
            ref={inputWrapperRef}
            className="piinteractiontitlepreview-variable-field"
          >
            <VariableTextOverlay
              segments={highlightedSegments}
              containerClassName="piinteractiontitlepreview-variable-overlay"
              contentClassName="piinteractiontitlepreview-variable-overlay-content"
              textClassName="text-interaction-primary"
              knownTokenClassName="text-interaction-secondary"
              unknownTokenClassName="text-creator-error"
              scrollTop={scrollTop}
              scrollLeft={scrollLeft}
            />
            <textarea
              ref={textareaRef}
              rows={1}
              className="piinteractiontitlepreview-variable-textarea"
              aria-label="piinteractiontitlepreview-input"
              value={inputValue}
              onFocus={handleFocus}
              onBlur={handleBlur}
              onChange={handleChange}
              onClick={handleClick}
              onKeyUp={handleKeyUp}
              onScroll={handleScroll}
              onWheel={handleWheel}
              onKeyDown={(e) => {
                if (e.key === "Enter" && suggestions.open) {
                  handleKeyDown(e);
                  return;
                }

                if (e.key === "Enter") {
                  e.preventDefault();
                  flushPendingValue();
                  e.currentTarget.blur();
                  return;
                }

                handleKeyDown(e);
              }}
              placeholder={placeholder}
            />
          </div>
        </div>
      </div>

      {suggestions.open && (
        <VariableSuggestionPopper
          open={true}
          anchorEl={suggestions.anchorEl ?? inputWrapperRef.current}
          width={inputWrapperRef.current?.clientWidth}
          maxWidth={520}
          suggestions={suggestions.filteredSuggestions}
          selectedIndex={suggestions.selectedIndex}
          menuRef={suggestions.menuRef}
          onSelect={suggestions.onSelect}
        />
      )}

      {warningText && (
        <div className="piinteractiontitlepreview-warning">{warningText}</div>
      )}
    </div>
  );
}
